import React from "react";
import { Reorder, useDragControls } from "framer-motion";
import { AlertTriangle, ArrowDown, ArrowUp, GripVertical, Plus, Trash2 } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { LEVEL_TITLE_MAX, LEVELS_MAX } from "../../pathways/builderModel";
import { fmtSessionShort } from "./format";
import { kindIcon, kindLabel, levelLabel } from "./labels";

/** DOM ids the activation banner scrolls to (see PathwayBuilder.jsx). */
export const levelAnchor = (key) => `pathway-level-anchor-${key}`;
export const stepAnchor = (key) => `pathway-step-anchor-${key}`;

/**
 * Builder step 2, Structure (spec screen 2, "the builder canvas").
 *
 *   Level type                                   Successive / Alternative
 *   ┌ ⠿ Level 1 · [Basics]      Unlocks: immediately ┐   one flat list, no level header
 *   │  ⠿ ▶ Breath fundamentals   [Mandatory]  ~12 min │   Successive: no pill (all mandatory),
 *   │  ⠿ ? Basics check          [Mandatory]   ~5 min │   and each row says what it unlocks after
 *   │  + Add step                                      │
 *   └──────────────────────────────────────────────────┘
 *   ┌ ⠿ Level 2 · [Practice]  Unlocks after: Level 1 · Basics ┐
 *   + Add level
 *
 * REORDERING, two ways. Drag a level or a step by its ⠿ handle (framer-motion `Reorder`:
 * levels among levels, steps within their level), or use the up / down buttons, which are
 * the keyboard path and the only way to move a step INTO the neighbouring level
 * (builderModel.moveStep crosses the boundary).
 *
 * A LIVE row names when or how the session happens (contract §10.11): the date and time
 * of a cohort session in the practitioner's zone ("Thu 12 Nov · 18:00"), or the session
 * type of a self-paced one, as the pathway's CURRENT schedule reads it.
 *
 * "UNLOCKS AFTER" is the builder's reading of the evaluator's rules (contract §3), so the
 * sequencing is visible while building: in `level`, level N+1 unlocks after level N; in
 * `successive`, each step unlocks after the one before it; in `alternative` everything is
 * open, and no note is shown.
 *
 * @param {object}  props
 * @param {object}  props.builder        usePathwayBuilder() result
 * @param {boolean} props.disabled
 * @param {(levelKey: string, step: object) => void} props.onOpenStep   edit an existing step
 * @param {(levelKey: string) => void} props.onAddStep                   open the editor on a new step
 * @param {Set<string>} props.problemStepKeys   steps an activation problem names
 * @param {Set<string>} props.problemLevelKeys  levels an activation problem names
 */
export default function StructureCanvas({ builder, disabled, onOpenStep, onAddStep, problemStepKeys, problemLevelKeys }) {
  const t = useT("admin");
  const { draft, errors } = builder;
  const levelType = draft.path_type === "level";
  const levelKeys = draft.levels.map((l) => l.key);

  if (!levelType) {
    const [only] = draft.levels;
    return (
      <div data-testid={IDS.canvas} className="bg-white rounded-3xl border border-eh-border-soft p-4 sm:p-5">
        <StepList level={only} builder={builder} disabled={disabled} onOpenStep={onOpenStep} problemStepKeys={problemStepKeys} />
        <AddStepButton levelKey={only.key} disabled={disabled} onAddStep={onAddStep} />
        <FieldError message={errors.levels ? t(errors.levels) : errors[`level:${only.key}:steps`] ? t(errors[`level:${only.key}:steps`]) : ""} />
      </div>
    );
  }

  return (
    <div data-testid={IDS.canvas} className="space-y-4">
      <Reorder.Group as="div" axis="y" values={levelKeys} onReorder={builder.reorderLevels} className="space-y-4">
        {draft.levels.map((level, i) => (
          <LevelBlock
            key={level.key}
            level={level}
            index={i}
            previous={i > 0 ? draft.levels[i - 1] : null}
            count={draft.levels.length}
            builder={builder}
            disabled={disabled}
            onOpenStep={onOpenStep}
            onAddStep={onAddStep}
            problemStepKeys={problemStepKeys}
            flagged={problemLevelKeys.has(level.key)}
          />
        ))}
      </Reorder.Group>
      <FieldError message={errors.levels ? t(errors.levels) : ""} />
      <button
        type="button"
        onClick={() => builder.addLevel()}
        disabled={disabled || draft.levels.length >= LEVELS_MAX}
        data-testid={IDS.addLevel}
        className="inline-flex items-center gap-1.5 rounded-full border border-eh-border bg-white px-4 py-2 text-sm font-semibold text-eh-ink hover:border-eh-ink disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <Plus className="w-4 h-4" aria-hidden="true" /> {t("path.structure.addLevel")}
      </button>
    </div>
  );
}

function LevelBlock({ level, index, previous, count, builder, disabled, onOpenStep, onAddStep, problemStepKeys, flagged }) {
  const t = useT("admin");
  const dragControls = useDragControls();
  const titleError = builder.errors[`level:${level.key}:title`];
  const stepsError = builder.errors[`level:${level.key}:steps`];
  const n = index + 1;
  const unlocks = previous
    ? t("path.structure.unlocksAfter", { what: levelLabel(t, index, previous.title.trim()) })
    : t("path.structure.unlocksNow");

  const remove = () => {
    if (level.steps.length > 0 && !window.confirm(t(level.steps.length === 1 ? "path.structure.confirmRemoveLevelOne" : "path.structure.confirmRemoveLevel", { n, count: level.steps.length }))) return;
    builder.removeLevel(level.key);
  };

  return (
    <Reorder.Item
      as="section"
      value={level.key}
      dragListener={false}
      dragControls={dragControls}
      id={levelAnchor(level.key)}
      tabIndex={-1}
      data-testid={IDS.level(level.key)}
      aria-label={levelLabel(t, n, level.title.trim())}
      className={`rounded-3xl border bg-white p-4 sm:p-5 outline-none ${flagged ? "border-amber-400 ring-1 ring-amber-300" : "border-eh-border-soft"}`}
    >
      <header className="flex flex-wrap items-center gap-2 mb-3">
        <DragHandle controls={dragControls} disabled={disabled} label={t("path.structure.dragLevel")} testId={IDS.levelDrag(level.key)} />
        <label htmlFor={`pathway-level-title-${level.key}`} className="text-sm font-semibold text-eh-ink whitespace-nowrap">
          {t("path.level.prefix", { n })}
        </label>
        <input
          id={`pathway-level-title-${level.key}`}
          value={level.title}
          onChange={(e) => builder.renameLevel(level.key, e.target.value)}
          placeholder={t("path.structure.levelTitlePh")}
          maxLength={LEVEL_TITLE_MAX}
          disabled={disabled}
          aria-invalid={Boolean(titleError)}
          data-testid={IDS.levelTitle(level.key)}
          className={`min-w-0 flex-1 bg-transparent text-sm font-semibold text-eh-ink rounded-md px-2 py-1 outline-none hover:bg-eh-cream/60 focus:bg-eh-cream/60 focus:ring-2 focus:ring-eh-sage-deep/30 ${titleError ? invalidInputClass : ""}`}
        />
        <span data-testid={IDS.levelUnlocks(level.key)} className="text-xs text-eh-muted ml-auto">{unlocks}</span>
        <div className="flex items-center">
          <IconButton label={t("path.structure.levelUp", { n })} disabled={disabled || index === 0} onClick={() => builder.moveLevel(level.key, -1)} testId={IDS.levelUp(level.key)}>
            <ArrowUp className="w-4 h-4" />
          </IconButton>
          <IconButton label={t("path.structure.levelDown", { n })} disabled={disabled || index === count - 1} onClick={() => builder.moveLevel(level.key, 1)} testId={IDS.levelDown(level.key)}>
            <ArrowDown className="w-4 h-4" />
          </IconButton>
          <IconButton label={t("path.structure.levelRemove", { n })} disabled={disabled || count <= 1} onClick={remove} testId={IDS.levelRemove(level.key)} danger>
            <Trash2 className="w-4 h-4" />
          </IconButton>
        </div>
      </header>
      <FieldError message={titleError ? t(titleError) : stepsError ? t(stepsError) : ""} />

      <StepList level={level} builder={builder} disabled={disabled} onOpenStep={onOpenStep} problemStepKeys={problemStepKeys} />
      <AddStepButton levelKey={level.key} disabled={disabled} onAddStep={onAddStep} />
    </Reorder.Item>
  );
}

function StepList({ level, builder, disabled, onOpenStep, problemStepKeys }) {
  const t = useT("admin");
  const { draft } = builder;
  if (level.steps.length === 0) {
    return <p className="text-sm text-eh-muted px-1 py-2">{t("path.structure.noSteps")}</p>;
  }
  // The step before each one in the flattened order, for Successive's "Unlocks after".
  const flat = draft.levels.flatMap((l) => l.steps);
  const previousOf = (key) => {
    const i = flat.findIndex((s) => s.key === key);
    return i > 0 ? flat[i - 1] : null;
  };
  return (
    <Reorder.Group
      as="ol"
      axis="y"
      values={level.steps.map((s) => s.key)}
      onReorder={(keys) => builder.reorderSteps(level.key, keys)}
      className="space-y-2"
    >
      {level.steps.map((step) => (
        <StepRow
          key={step.key}
          step={step}
          levelKey={level.key}
          builder={builder}
          disabled={disabled}
          onOpenStep={onOpenStep}
          previous={previousOf(step.key)}
          flagged={problemStepKeys.has(step.key)}
        />
      ))}
    </Reorder.Group>
  );
}

function StepRow({ step, levelKey, builder, disabled, onOpenStep, previous, flagged }) {
  const t = useT("admin");
  const { lang } = useLanguage();
  const dragControls = useDragControls();
  const pathType = builder.draft.path_type;
  const Icon = kindIcon(step.kind);
  const minutes = Number(step.duration_minutes) || 0;
  const title = step.title.trim() || t("path.structure.untitledStep");
  const invalid = Boolean(builder.errors[`step:${step.key}`]);
  const mandatory = step.required;

  return (
    <Reorder.Item
      as="li"
      value={step.key}
      dragListener={false}
      dragControls={dragControls}
      id={stepAnchor(step.key)}
      tabIndex={-1}
      data-testid={IDS.step(step.key)}
      data-kind={step.kind}
      className={`flex flex-wrap items-center gap-2 rounded-2xl border bg-eh-cream/50 px-3 py-2 outline-none ${
        flagged || invalid ? "border-amber-400" : "border-eh-border-soft"
      }`}
    >
      <DragHandle controls={dragControls} disabled={disabled} label={t("path.structure.dragStep")} testId={IDS.stepDrag(step.key)} />
      <Icon className="w-4 h-4 text-eh-muted flex-shrink-0" aria-label={kindLabel(t, step.kind)} />
      <button
        type="button"
        onClick={() => onOpenStep(levelKey, step)}
        data-testid={IDS.stepOpen(step.key)}
        className="min-w-0 flex-1 text-left text-sm text-eh-ink hover:underline truncate"
      >
        {title}
      </button>
      {step.kind === "live" && (
        <span data-testid={IDS.stepLive(step.key)} className="text-xs text-eh-muted whitespace-nowrap">
          {liveLabel(t, step.live, builder.draft.schedule, lang)}
        </span>
      )}
      {(flagged || invalid) && (
        <span data-testid={IDS.stepInvalid(step.key)} className="inline-flex items-center gap-1 text-xs text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
          {t("path.structure.needsAttention")}
        </span>
      )}
      {pathType === "successive" && (
        <span data-testid={IDS.stepUnlocks(step.key)} className="text-xs text-eh-muted">
          {previous ? t("path.structure.unlocksAfter", { what: previous.title.trim() || t("path.structure.untitledStep") }) : t("path.structure.unlocksNow")}
        </span>
      )}
      {pathType !== "successive" && (
        <button
          type="button"
          onClick={() => builder.toggleRequired(step.key)}
          disabled={disabled}
          aria-pressed={mandatory}
          aria-label={t(mandatory ? "path.structure.pillMandatoryAria" : "path.structure.pillOptionalAria", { title })}
          data-testid={IDS.stepPill(step.key)}
          className={`text-xs rounded-full px-2.5 py-0.5 border disabled:opacity-60 ${
            mandatory ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-eh-ochre-deep text-eh-ochre-deep"
          }`}
        >
          {t(mandatory ? "path.step.mandatory" : "path.step.optional")}
        </button>
      )}
      {minutes > 0 && (
        <span data-testid={IDS.stepDuration(step.key)} className="text-xs text-eh-muted tabular-nums w-14 text-right">
          {t("path.step.minutes", { n: minutes })}
        </span>
      )}
      <div className="flex items-center">
        <IconButton label={t("path.structure.stepUp", { title })} disabled={disabled || !builder.canMove(step.key, -1)} onClick={() => builder.moveStep(step.key, -1)} testId={IDS.stepUp(step.key)}>
          <ArrowUp className="w-4 h-4" />
        </IconButton>
        <IconButton label={t("path.structure.stepDown", { title })} disabled={disabled || !builder.canMove(step.key, 1)} onClick={() => builder.moveStep(step.key, 1)} testId={IDS.stepDown(step.key)}>
          <ArrowDown className="w-4 h-4" />
        </IconButton>
        <IconButton
          label={t("path.structure.stepRemove", { title })}
          disabled={disabled}
          onClick={() => { if (window.confirm(t("path.structure.confirmRemoveStep", { title }))) builder.removeStep(step.key); }}
          testId={IDS.stepRemove(step.key)}
          danger
        >
          <Trash2 className="w-4 h-4" />
        </IconButton>
      </div>
    </Reorder.Item>
  );
}

/** A live row's "when": the cohort instant, or the self-paced session type; a prompt while unset. */
function liveLabel(t, live, schedule, lang) {
  if (schedule === "cohort") return fmtSessionShort(live.starts_at, lang) || t("path.structure.liveNoTime");
  return live.service_id ? (live.service?.name || t("path.editor.live.serviceUnnamed")) : t("path.structure.liveNoService");
}

function AddStepButton({ levelKey, disabled, onAddStep }) {
  const t = useT("admin");
  return (
    <button
      type="button"
      onClick={() => onAddStep(levelKey)}
      disabled={disabled}
      data-testid={IDS.addStep(levelKey)}
      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-eh-ochre-deep hover:text-eh-ink disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <Plus className="w-4 h-4" aria-hidden="true" /> {t("path.structure.addStep")}
    </button>
  );
}

function DragHandle({ controls, disabled, label, testId }) {
  return (
    <button
      type="button"
      onPointerDown={(e) => { if (!disabled) controls.start(e); }}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      className="p-1 rounded text-eh-muted/70 hover:text-eh-ink hover:bg-white cursor-grab active:cursor-grabbing touch-none disabled:opacity-30 disabled:cursor-not-allowed"
    >
      <GripVertical className="w-4 h-4" aria-hidden="true" />
    </button>
  );
}

function IconButton({ label, onClick, disabled, testId, danger = false, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-testid={testId}
      className={`p-1.5 rounded disabled:opacity-30 disabled:cursor-not-allowed ${
        danger ? "text-rose-600 hover:bg-rose-50" : "text-eh-muted hover:bg-white hover:text-eh-ink"
      }`}
    >
      {children}
    </button>
  );
}
